import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Chrome } from './components/Chrome';
import { RoomRack, RoomRackLegend, RoomRackStats } from './components/RoomRack';
import { NewStayModal } from './components/NewStayModal';
import { StayDetailPanel } from './components/StayDetail';
import { StaysList } from './components/StaysList';
import { FreeBedChoiceModal } from './components/FreeBedChoiceModal';
import { BlockBedModal } from './components/BlockBedModal';
import { BlockDetailModal } from './components/BlockDetailModal';
import { HousekeepingFlipModal } from './components/HousekeepingFlipModal';
import { ProfileSearchModal } from './components/ProfileSearchModal';
import { ProfileDetailPanel } from './components/ProfileDetailPanel';
import { HousekeepingScreen } from './components/HousekeepingScreen';
import { RoomTrackerScreen } from './components/RoomTrackerScreen';
import { OverdueBanner } from './components/OverdueBanner';
import { CleaningNotesModal } from './components/CleaningNotesModal';
import { BookingsProvider } from './lib/bookingsContext';
import { HousekeepingLogModal } from './components/HousekeepingLogModal';
import { LoginScreen } from './components/LoginScreen';
import { ChangePasswordModal } from './components/ChangePasswordModal';
import { HousekeeperLanding } from './components/HousekeeperLanding';
import { KeysScreen } from './components/KeysScreen';
import { AvailabilityScreen } from './components/AvailabilityScreen';
import { useAuth } from './lib/auth';
import { PortalApp } from './portal/PortalApp';
import { api, type Room, type Bed, type Property } from './lib/api';
import { setPropertyTimezone, fmtClock } from './lib/format';

const REFRESH_MS = 30_000;
type Tab = 'rack' | 'tracker' | 'stays' | 'housekeeping' | 'keys' | 'availability';

export default function App() {
  // Portal route — entirely separate UI for service providers
  // Must come BEFORE any staff auth logic
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/portal')) {
    return <PortalApp />;
  }

  const { user, loading: authLoading, logout } = useAuth();

  // Auth gates — render before the rest of the app boots
  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                    height: '100vh', fontSize: 14, color: 'var(--text-secondary)' }}>
        Loading…
      </div>
    );
  }
  if (!user) return <LoginScreen />;
  if (user.role === 'HOUSEKEEPING') {
    // Housekeepers get their own dedicated mobile-friendly UI
    // (still subject to the mustChangePassword check via the overlay below)
    if (user.mustChangePassword) {
      return <ChangePasswordModal forced />;
    }
    return <HousekeeperLanding />;
  }

  const [property, setProperty] = useState<Property | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [apiOnline, setApiOnline] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<string>('—');
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('rack');

  // Overlays: choice picker → either New Reservation modal OR Block modal
  const [choiceBed, setChoiceBed] = useState<{ bed: Bed; room: Room } | null>(null);
  const [reserveBed, setReserveBed] = useState<{ bed: Bed; room: Room } | null>(null);
  const [blockBed, setBlockBed]     = useState<{ bed: Bed; room: Room } | null>(null);
  const [detailStayId, setDetailStayId] = useState<string | null>(null);
  const [detailBlockId, setDetailBlockId] = useState<string | null>(null);
  const [hskpRoom, setHskpRoom] = useState<Room | null>(null);
  const [profileSearchOpen, setProfileSearchOpen] = useState(false);
  const [detailProfileId, setDetailProfileId] = useState<string | null>(null);
  const [hskpLogRoomId, setHskpLogRoomId] = useState<string | null>(null);
  const [notesRoom, setNotesRoom] = useState<Room | null>(null);
  // Bumped so the housekeeping table reloads its counts/notes after a change.
  const [hskpRefresh, setHskpRefresh] = useState(0);

  useEffect(() => {
    api.property()
      .then(p => { setProperty(p); setPropertyTimezone(p.timezone); })
      .catch(() => setApiOnline(false));
  }, []);

  const loadRooms = useCallback(async () => {
    try {
      const r = await api.rooms();
      setRooms(r);
      setApiOnline(true);
      setError(null);
      setLastRefresh('Last refreshed: ' + fmtClock(new Date()));
    } catch (e) {
      setApiOnline(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    loadRooms();
    const id = setInterval(loadRooms, REFRESH_MS);
    return () => clearInterval(id);
  }, [loadRooms]);

  // One rooms fetch feeds every tab, so the split happens here. Room Plan and
  // the stay flow it launches deal in beds, so they take accommodation
  // rooms; Room Tracker takes booking rooms. Housekeeping and Keys take the
  // whole list deliberately — a booking-only room still needs cleaning and
  // still has a door.
  const accommodationRooms = useMemo(() => rooms.filter(r => r.supportsAccommodation), [rooms]);
  const bookingRooms = useMemo(() => rooms.filter(r => r.supportsBookings), [rooms]);

  const handleBedClick = useCallback((room: Room, bed: Bed) => {
    if (bed.isBlocked && bed.activeBlock) {
      setDetailBlockId(bed.activeBlock.id);
      return;
    }
    if (bed.currentStay) {
      setDetailStayId(bed.currentStay.id);
      return;
    }
    // Free → show the choice picker
    setChoiceBed({ room, bed });
  }, []);

  const roleLabel = user.role.charAt(0) + user.role.slice(1).toLowerCase();
  const titleText = property
    ? `KATARA GROUP PMS — [${property.name}] — ${roleLabel}`
    : 'KATARA GROUP PMS — Loading…';
  const propertyLabel = property
    ? `${property.entity.name} · ${property.name} (${property.code})`
    : '—';

  return (
    <BookingsProvider>
      <Chrome
        onProfileSearch={() => setProfileSearchOpen(true)}
        titleText={titleText}
        apiOnline={apiOnline}
        apiStatusLabel={apiOnline ? 'Connected' : 'Disconnected'}
        userLabel={`${user.fullName} · ${user.role}`}
        onLogout={logout}
        propertyLabel={propertyLabel}
        lastRefresh={lastRefresh}
        onRefresh={() => { loadRooms(); }}
        activeTab={tab}
        onTabChange={setTab}
      >
        {/* Above every tab: the alarm rings app-wide, so it needs a destination. */}
        <OverdueBanner isTrackerActive={tab === 'tracker'} onGoToTracker={() => setTab('tracker')} />

        {tab === 'rack' && (
          <>
            <div className="content-header">
              <h1>Room plan</h1>
              <RoomRackStats rooms={accommodationRooms} />
            </div>
            {error && <div className="error">Could not load rooms: {error}</div>}
            <RoomRack rooms={accommodationRooms} onBedClick={handleBedClick} onHousekeepingClick={setHskpRoom} />
            <RoomRackLegend />
          </>
        )}

        {tab === 'tracker' && (
          <>
            <div className="content-header">
              <h1>Room tracker</h1>
            </div>
            {error && <div className="error">Could not load rooms: {error}</div>}
            <RoomTrackerScreen rooms={bookingRooms} onRoomsChanged={loadRooms} />
          </>
        )}

        {tab === 'stays' && (
          <>
            <div className="content-header">
              <h1>Accommodation Bookings</h1>
            </div>
            <StaysList onSelect={setDetailStayId} />
          </>
        )}

        {tab === 'housekeeping' && (
          <>
            <div className="content-header">
              <h1>Housekeeping</h1>
            </div>
            <HousekeepingScreen
              rooms={rooms}
              onStatusClick={setHskpRoom}
              onHistoryClick={setHskpLogRoomId}
              onNotesClick={setNotesRoom}
              refreshToken={hskpRefresh}
            />
          </>
        )}
        {tab === 'keys' && (
          <KeysScreen />
        )}
        {tab === 'availability' && (
          <AvailabilityScreen />
        )}
      </Chrome>

      {choiceBed && (
        <FreeBedChoiceModal
          bed={choiceBed.bed}
          room={choiceBed.room}
          onClose={() => setChoiceBed(null)}
          onChoose={(action) => {
            const cb = choiceBed;
            setChoiceBed(null);
            if (action === 'reserve') setReserveBed(cb);
            else                       setBlockBed(cb);
          }}
        />
      )}

      {reserveBed && (
        <NewStayModal
          bed={reserveBed.bed}
          room={reserveBed.room}
          onClose={() => setReserveBed(null)}
          onCreated={() => { setReserveBed(null); loadRooms(); }}
        />
      )}

      {blockBed && (
        <BlockBedModal
          bed={blockBed.bed}
          room={blockBed.room}
          onClose={() => setBlockBed(null)}
          onCreated={() => { setBlockBed(null); loadRooms(); }}
        />
      )}

      {detailStayId && (
        <StayDetailPanel
          stayId={detailStayId}
          onClose={() => setDetailStayId(null)}
          onChanged={loadRooms}
        />
      )}

      {detailBlockId && (
        <BlockDetailModal
          blockId={detailBlockId}
          onClose={() => setDetailBlockId(null)}
          onChanged={() => { setDetailBlockId(null); loadRooms(); }}
        />
      )}

      {hskpRoom && (
        <HousekeepingFlipModal
          roomId={hskpRoom.id}
          roomNumber={hskpRoom.number}
          roomDisplayName={hskpRoom.displayName}
          currentStatus={hskpRoom.housekeepingStatus as any}
          onClose={() => setHskpRoom(null)}
          onChanged={() => { setHskpRoom(null); loadRooms(); }}
        />
      )}

      {profileSearchOpen && (
        <ProfileSearchModal
          onClose={() => setProfileSearchOpen(false)}
          onSelect={(id) => { setProfileSearchOpen(false); setDetailProfileId(id); }}
        />
      )}

      {detailProfileId && (
        <ProfileDetailPanel
          profileId={detailProfileId}
          onClose={() => setDetailProfileId(null)}
          onChanged={loadRooms}
        />
      )}

      {hskpLogRoomId && (
        <HousekeepingLogModal
          roomId={hskpLogRoomId}
          onClose={() => setHskpLogRoomId(null)}
        />
      )}

      {notesRoom && (
        <CleaningNotesModal
          roomId={notesRoom.id}
          roomNumber={notesRoom.number}
          roomDisplayName={notesRoom.displayName}
          onClose={() => setNotesRoom(null)}
          onChanged={() => setHskpRefresh(n => n + 1)}
        />
      )}

      {user.mustChangePassword && (
        <ChangePasswordModal forced />
      )}
    </BookingsProvider>
  );
}
